import { Panel } from '../components/Panel';
import { IconPencilSquare, IconPlus, IconTrash } from '../components/icons';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';

const pl = {
  save: 'Zapisz',
  cancel: 'Anuluj',
  signIn: 'Zaloguj się',
  add: 'Dodaj',
  edit: 'Edytuj',
  remove: 'Usuń',
  small: 'Mały',
  name: 'Nazwa',
  amount: 'Kwota',
  date: 'Termin',
  done: 'Zrobione',
  meal: 'Posiłek',
  hint: 'Pola z gwiazdką są wymagane.',
  section: 'Nagłówek sekcji',
  link: 'Link w tekście',
  meta: 'Zmieniono 12.10.2026, 14:05',
  narrow: 'Wąska strona: logowanie, profil.',
  regular: 'Zwykła strona.',
  wide: 'Szeroka strona: aplikacja z paskiem bocznym.',
  bottomBar:
    'Na telefonie dokłada miejsce na dolny pasek, żeby nie przykrył końca strony.',
  options: ['Śniadanie', 'Obiad', 'Kolacja'],
  anchor:
    'Otwiera się tuż pod swoją kotwicą — tu jest nią to pole. Menu i pole z podpowiedziami dokładają własną szerokość i zaokrąglenia.',
};

const sample: Dictionary<typeof pl> = {
  pl,
  en: {
    save: 'Save',
    cancel: 'Cancel',
    signIn: 'Sign in',
    add: 'Add',
    edit: 'Edit',
    remove: 'Delete',
    small: 'Small',
    name: 'Name',
    amount: 'Amount',
    date: 'Due',
    done: 'Done',
    meal: 'Meal',
    hint: 'Fields marked with an asterisk are required.',
    section: 'Section heading',
    link: 'An inline link',
    meta: 'Updated 12.10.2026, 14:05',
    narrow: 'Narrow page: sign-in, profile.',
    regular: 'Regular page.',
    wide: 'Wide page: an app with a sidebar.',
    bottomBar:
      'On a phone, adds room for the bottom bar so it never covers the end of the page.',
    options: ['Breakfast', 'Lunch', 'Dinner'],
    anchor:
      'Opens just below its anchor — here, this canvas. Menu and Combobox each add their own width and corners.',
  },
};

export default {
  kind: 'primitives',
  title: { pl: 'Prymitywy', en: 'Primitives' },
  description: {
    pl: 'Klasy z `styles/primitives.css`, które wolno pisać bezpośrednio w dowolnym pliku: przyciski, formularze, tekst, kontenery stron i powierzchnia rozwijanej listy. Wszystko inne idzie przez komponent.',
    en: 'The classes in `styles/primitives.css`, meant to be written directly in any file: buttons, forms, text, page containers and the surface a dropdown opens on. Everything else goes through a component.',
  },
  variants: [
    {
      name: { pl: 'Przyciski', en: 'Buttons' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <div className="form-stack">
            <div className="form-actions">
              <button type="button" className="btn-ghost">
                {t.cancel}
              </button>
              <button type="button" className="btn-primary">
                {t.save}
              </button>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-ghost btn-sm">
                {t.small}
              </button>
              <button type="button" className="btn-primary btn-sm">
                {t.small}
              </button>
            </div>
            <div className="row-actions">
              <button
                type="button"
                className="btn-primary-icon"
                aria-label={t.add}
              >
                <IconPlus />
              </button>
              <button
                type="button"
                className="btn-icon-ghost"
                aria-label={t.edit}
              >
                <IconPencilSquare />
              </button>
              <button
                type="button"
                className="btn-icon-ghost is-active"
                aria-label={t.edit}
              >
                <IconPencilSquare />
              </button>
              <button
                type="button"
                className="btn-icon-ghost btn-icon-overlay"
                aria-label={t.edit}
              >
                <IconPencilSquare />
              </button>
              <button
                type="button"
                className="btn-icon-danger"
                aria-label={t.remove}
              >
                <IconTrash />
              </button>
            </div>
            <button type="button" className="btn-primary-block">
              {t.signIn}
            </button>
          </div>
        );
      },
    },
    {
      name: { pl: 'Formularz', en: 'Form' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <form className="form-stack" onSubmit={(e) => e.preventDefault()}>
            <p className="form-hint">{t.hint}</p>
            <div className="form-grid-3plus">
              <div>
                <label htmlFor="story-primitive-name" className="field-label">
                  {t.name} *
                </label>
                <input id="story-primitive-name" className="field-input" />
              </div>
              <div>
                <label htmlFor="story-primitive-amount" className="field-label">
                  {t.amount}
                </label>
                <input
                  id="story-primitive-amount"
                  className="field-input"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label htmlFor="story-primitive-date" className="field-label">
                  {t.date}
                </label>
                <input
                  id="story-primitive-date"
                  type="date"
                  className="field-input"
                />
              </div>
              <button type="submit" className="btn-primary">
                {t.add}
              </button>
            </div>
            <div>
              <label htmlFor="story-primitive-meal" className="field-label">
                {t.meal}
              </label>
              <select id="story-primitive-meal" className="field-select">
                {t.options.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </div>
            <label className="field-label">
              <input type="checkbox" className="checkbox-violet" /> {t.done}
            </label>
            <div className="form-actions">
              <button type="button" className="btn-ghost">
                {t.cancel}
              </button>
              <button type="submit" className="btn-primary">
                {t.save}
              </button>
            </div>
          </form>
        );
      },
    },
    {
      name: { pl: 'Tekst', en: 'Text' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <div className="form-stack">
            <h2 className="heading-section">{t.section}</h2>
            <p>
              <a href="#text" className="text-link">
                {t.link}
              </a>
            </p>
            <p className="text-hint">{t.meta}</p>
            <p className="wordmark">
              IT<span className="wordmark-accent">Lamb</span>
            </p>
            <p className="wordmark-md">
              IT<span className="wordmark-accent">Lamb</span>
            </p>
            <p className="wordmark-lg">
              IT<span className="wordmark-accent">Lamb</span>
            </p>
          </div>
        );
      },
    },
    {
      name: { pl: 'Kontenery stron', en: 'Page containers' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <>
            <div className="page-narrow">
              <Panel title="page-narrow">{t.narrow}</Panel>
            </div>
            <div className="page">
              <Panel title="page">{t.regular}</Panel>
            </div>
            <div className="page-wide page-with-bottom-bar">
              <Panel title="page-wide page-with-bottom-bar">
                <p>{t.wide}</p>
                <p className="text-hint">{t.bottomBar}</p>
              </Panel>
            </div>
          </>
        );
      },
    },
    {
      // Last on the page: the canvas is the popover's anchor, and it opens
      // below it.
      name: { pl: 'Powierzchnia listy', en: 'Popover' },
      render: (locale) => (
        <>
          <p className="text-hint">{sample[locale].anchor}</p>
          <ul className="popover">
            {sample[locale].options.map((option) => (
              <li key={option}>
                <button type="button" className="btn-icon-ghost">
                  {option}
                </button>
              </li>
            ))}
          </ul>
        </>
      ),
    },
  ],
} satisfies Story;
